<template>
  <div class="contents">
    <!-- Filters - Desktop and Mobile -->
    <div class="mb-0">
      <div
        class="-mx-4 flex lg:-mx-0 lg:overflow-visible lg:px-0 overflow-x-auto pb-2 px-4 relative scrollbar-hide space-x-3"
      >
        <div
          v-for="filter in filters"
          :key="filter.id"
        >
          <!-- Desktop: popover for filters with options -->
          <UPopover
            v-if="filter.hasDropdown"
            :content="filterPopoverContent"
            :ui="filterPopoverUi"
          >
            <button
              type="button"
              class="cursor-pointer duration-200 font-medium hidden items-center lg:flex px-4 py-2 rounded-full space-x-2 text-sm transition-all whitespace-nowrap"
              :class="[
                filter.isActive
                  ? 'bg-primary text-content-inverse shadow-lg'
                  : 'bg-surface-canvas dark:bg-surface-level-800 border border-border-level-300 dark:border-border-level-600 text-content-level-700 dark:text-content-level-300 hover:bg-surface-level-50 dark:hover:bg-surface-level-700'
              ]"
            >
              <component
                :is="filter.icon"
                v-if="filter.icon"
                class="h-4 w-4"
                :class="filter.iconColor || ''"
              />

              <span>{{ filter.label }}</span>

              <ChevronDownIcon class="h-4 w-4" />
            </button>

            <template #content>
              <div class="p-4">
                <!-- Filter Title -->
                <h3 class="dark:text-content-inverse font-semibold mb-4 text-content-level-900">
                  {{ filter.label }}
                </h3>

                <!-- Filter Options -->
                <div class="mt-4 space-y-3">
                  <div
                    v-for="option in getFilterOptions(filter.id)"
                    :key="option.id"
                    class="flex items-center justify-between"
                  >
                    <label
                      class="cursor-pointer flex items-center space-x-3"
                      @click.prevent="() => toggleFilterOption(filter.id, option.id)"
                    >
                      <input
                        v-if="filter.id === 'ratings'"
                        type="radio"
                        name="ratings"
                        :checked="isOptionSelected(filter.id, option.id)"
                        class="radio radio-primary radio-sm"
                        @change="() => toggleFilterOption(filter.id, option.id)"
                      />

                      <SharedAppCheckbox
                        v-else
                        size="sm"
                        :model-value="isOptionSelected(filter.id, option.id)"
                        readonly
                        class="pointer-events-none"
                      />

                      <span class="dark:text-content-level-300 text-content-level-700 text-sm">
                        <span
                          v-if="filter.id === 'ratings'"
                          class="flex items-center space-x-1"
                        >
                          <span class="font-medium">{{ option.id }}</span>

                          <svg
                            class="fill-icon-current h-4 text-warning-400 w-4"
                            viewBox="0 0 20 20"
                          >
                            <path
                              d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0
              00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0
              00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54
              1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8
              2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0
              00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0
              00.951-.69l1.07-3.292z"
                            />
                          </svg>

                          <span v-if="option.id !== '5'">
                            {{ $t('commerce.filterButtons.orMore') }}
                          </span>
                        </span>

                        <span
                          v-else
                          class="flex items-center space-x-2"
                        >
                          <DietaryIcon
                            v-if="option.icon"
                            :name="option.icon"
                          />

                          <span>{{ option.label }}</span>
                        </span>
                      </span>
                    </label>

                    <span
                      v-if="option.value"
                      class="dark:text-content-level-400 text-content-level-500 text-xs"
                    >
                      {{ option.value }}
                    </span>
                  </div>
                </div>

                <!-- Action Buttons -->
                <div
                  class="border-border-level-200 border-t dark:border-border-level-700 mt-4 pt-3"
                >
                  <!-- For the dietary filter: Show Clear and Apply buttons -->
                  <div
                    v-if="filter.id === 'dietary'"
                    class="flex justify-end space-x-3"
                  >
                    <button
                      type="button"
                      class="bg-surface-canvas border border-border-level-300 btn btn-active dark:bg-surface-clear dark:border-border-level-600"
                      @click="() => clearFilter(filter.id)"
                    >
                      {{ $t('commerce.filterButtons.clearFilter') }}
                    </button>

                    <button
                      type="button"
                      class="btn btn-active btn-primary text-content-inverse"
                      @click="() => applyFilter(filter.id)"
                    >
                      {{ $t('commerce.filterButtons.applyFilter') }}
                    </button>
                  </div>

                  <!-- For other filters: Show only Clear button -->
                  <div
                    v-else
                    class="flex justify-end"
                  >
                    <button
                      type="button"
                      class="bg-surface-canvas border border-border-level-300 btn btn-active dark:bg-surface-clear dark:border-border-level-600"
                      @click="() => clearFilter(filter.id)"
                    >
                      {{ $t('commerce.filterButtons.clearFilter') }}
                    </button>
                  </div>
                </div>
              </div>
            </template>
          </UPopover>

          <!-- Desktop: toggle button for filters without options -->
          <button
            v-else
            type="button"
            class="cursor-pointer duration-200 flex font-medium items-center lg:px-4 lg:py-2 px-3 py-1 rounded-full space-x-2 text-sm transition-all whitespace-nowrap"
            :class="[
              filter.isActive
                ? 'bg-primary text-content-inverse shadow-lg'
                : 'bg-surface-canvas dark:bg-surface-level-800 border border-border-level-300 dark:border-border-level-600 text-content-level-700 dark:text-content-level-300 hover:bg-surface-level-50 dark:hover:bg-surface-level-700'
            ]"
            @click="() => handleFilterClick(filter)"
          >
            <component
              :is="filter.icon"
              v-if="filter.icon"
              class="h-4 w-4"
              :class="filter.iconColor || ''"
            />

            <span>{{ filter.label }}</span>
          </button>

          <!-- Mobile: Button that opens modal -->
          <button
            v-if="filter.hasDropdown"
            type="button"
            class="cursor-pointer duration-200 flex font-medium items-center lg:hidden px-3 py-1 rounded-full space-x-2 text-sm transition-all whitespace-nowrap"
            :class="[
              filter.isActive
                ? 'bg-primary text-content-inverse shadow-lg'
                : 'bg-surface-canvas dark:bg-surface-level-800 border border-border-level-300 dark:border-border-level-600 text-content-level-700 dark:text-content-level-300 hover:bg-surface-level-50 dark:hover:bg-surface-level-700'
            ]"
            @click="() => openFilterModal(filter)"
          >
            <component
              :is="filter.icon"
              v-if="filter.icon"
              class="h-4 w-4"
              :class="filter.iconColor || ''"
            />

            <span>{{ filter.label }}</span>

            <ChevronDownIcon class="h-4 w-4" />
          </button>
        </div>

        <!-- Other Filters Button -->
        <button
          type="button"
          class="cursor-pointer duration-200 flex font-medium items-center lg:px-4 lg:py-2 px-3 py-1 rounded-full space-x-2 text-sm transition-all whitespace-nowrap"
          :class="[
            isOtherFiltersActive
              ? 'bg-primary text-content-inverse shadow-lg'
              : 'bg-surface-canvas dark:bg-surface-level-800 border border-border-level-300 dark:border-border-level-600 text-content-level-700 dark:text-content-level-300 hover:bg-surface-level-50 dark:hover:bg-surface-level-700'
          ]"
          @click="openMoreFiltersModal"
        >
          <EllipsisHorizontalIcon class="h-4 w-4" />

          <span>
            {{ $t('commerce.filterButtons.otherFilters') }}
          </span>

          <span
            v-if="activeFiltersCount && activeFiltersCount > 0"
            class="bg-primary font-semibold inline-flex items-center justify-center rounded-full size-5 text-content-inverse text-xs"
          >
            {{ activeFiltersCount }}
          </span>
        </button>
      </div>
    </div>

    <!-- Filter Modal Component (kept for potential future use) -->
    <FilterModal
      :is-open="showFilterModal"
      @close="closeFilterModalOverlay"
      @apply-filters="handleApplyFilters"
    />

    <!-- More Filters Modal Component -->
    <MoreFiltersModal
      :is-open="showMoreFiltersModal"
      @close="closeMoreFiltersModal"
    />

    <BaseModal
      :model-value="isFilterModalOpen"
      :title="activeFilterModal?.label ?? ''"
      max-width-class="max-w-md"
      @close="closeFilterModal"
      @after-leave="clearActiveFilterModal"
    >
      <!-- Filter Options -->
      <div class="space-y-3">
        <div
          v-for="option in getFilterOptions(activeFilterModal?.id)"
          :key="option.id"
          class="flex items-center justify-between"
        >
          <label
            class="cursor-pointer flex items-center space-x-3"
            @click.prevent="() => toggleFilterOption(activeFilterModal?.id, option.id)"
          >
            <input
              v-if="activeFilterModal?.id === 'ratings'"
              type="radio"
              name="ratings"
              :checked="isOptionSelected(activeFilterModal?.id, option.id)"
              class="radio radio-primary radio-sm"
              @change="() => toggleFilterOption(activeFilterModal?.id, option.id)"
            />

            <SharedAppCheckbox
              v-else
              size="sm"
              :model-value="isOptionSelected(activeFilterModal?.id, option.id)"
              readonly
              class="pointer-events-none"
            />

            <span class="dark:text-content-level-300 text-content-level-700 text-sm">
              <span
                v-if="activeFilterModal?.id === 'ratings'"
                class="flex items-center space-x-1"
              >
                <span class="font-medium">{{ option.id }}</span>

                <svg
                  class="fill-icon-current h-4 text-warning-400 w-4"
                  viewBox="0 0 20 20"
                >
                  <path
                    d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0
              00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0
              00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54
              1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8
              2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0
              00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0
              00.951-.69l1.07-3.292z"
                  />
                </svg>

                <span v-if="option.id !== '5'">
                  {{ $t('commerce.filterButtons.orMore') }}
                </span>
              </span>

              <span
                v-else
                class="flex items-center space-x-2"
              >
                <DietaryIcon
                  v-if="option.icon"
                  :name="option.icon"
                  class="h-4 w-4"
                />

                <span>{{ option.label }}</span>
              </span>
            </span>
          </label>

          <span
            v-if="option.value"
            class="dark:text-content-level-400 text-content-level-500 text-xs"
          >
            {{ option.value }}
          </span>
        </div>
      </div>

      <template #footer>
        <button
          type="button"
          class="bg-surface-canvas border border-border-level-300 btn btn-active dark:bg-surface-clear dark:border-border-level-600"
          @click="clearActiveFilter"
        >
          {{ $t('commerce.filterButtons.clearFilter') }}
        </button>

        <button
          v-if="activeFilterModal?.id === 'dietary'"
          type="button"
          class="btn btn-active btn-primary text-content-inverse"
          @click="applyActiveFilter"
        >
          {{ $t('commerce.filterButtons.applyFilter') }}
        </button>
      </template>
    </BaseModal>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import type { Component } from 'vue'
import type { PopoverProps } from '@nuxt/ui'
import { UPopover } from '#components'
import {
  ChevronDownIcon,
  EllipsisHorizontalIcon,
  SparklesIcon,
  StarIcon
} from '@heroicons/vue/24/outline'
import DietaryIcon from '~/components/DietaryIcon.vue'
import BaseModal from '~/components/shared/dialogs/BaseModal.vue'
import SharedAppCheckbox from '~/components/shared/AppCheckbox.vue'
import FilterModal from './FilterModal.vue'
import MoreFiltersModal from './MoreFiltersModal.vue'

// Import stores
import { useFiltersStore } from '../stores/useFiltersStore'

type FilterValue = string | number
type FilterId = 'reliability' | 'ratings' | 'dietary' | 'other'
type FilterOptionGroupId = 'ratings' | 'dietary'
type DietaryIconName =
  | 'gluten-free'
  | 'dairy-free'
  | 'nut-free'
  | 'vegetarian'
  | 'vegan'
  | 'low-carb'
  | 'keto'
  | 'halal'
  | 'kosher'

interface FilterDefinition {
  id: FilterId
  label: string
  icon?: Component
  iconColor?: string
  isActive: boolean
  hasDropdown: boolean
}

interface FilterOption {
  id: FilterValue
  label: string
  value: string | null
  icon?: DietaryIconName | null
}

interface FilterOptions {
  ratings: FilterOption[]
  dietary: FilterOption[]
}

interface SelectedFilterOptions {
  ratings: FilterValue[]
  dietary: FilterValue[]
}

interface FilterModalPayload {
  dietary?: FilterValue[]
  otherFilters?: string[]
}

defineProps<{
  /** Number of active filters. */
  activeFiltersCount?: number
}>()

const emit = defineEmits<{
  filterToggleRequested: [filter: FilterDefinition]
}>()

const filterPopoverContent: PopoverProps['content'] = { align: 'center', sideOffset: 12 }
const filterPopoverUi: PopoverProps['ui'] = {
  content: 'max-w-md overflow-hidden rounded-3xl text-sm/6 w-96'
}

// Modal state
const showFilterModal = ref(false)
const showMoreFiltersModal = ref(false)
const activeFilterModal = ref<FilterDefinition | null>(null)
const openMoreFiltersModal = () => {
  showMoreFiltersModal.value = true
}
const closeFilterModalOverlay = () => {
  showFilterModal.value = false
}
const closeMoreFiltersModal = () => {
  showMoreFiltersModal.value = false
}
const clearActiveFilterModal = () => {
  activeFilterModal.value = null
}
const clearActiveFilter = () => {
  const id = activeFilterModal.value?.id
  if (id) clearFilterAndClose(id)
}
const applyActiveFilter = () => {
  const id = activeFilterModal.value?.id
  if (id) applyFilterAndClose(id)
}
const isFilterModalOpen = ref(false)

// Open filter modal
const openFilterModal = (filter: FilterDefinition): void => {
  activeFilterModal.value = filter
  isFilterModalOpen.value = true
}

// Close filter modal
const closeFilterModal = (): void => {
  isFilterModalOpen.value = false
  // activeFilterModal clears on the modal's afterLeave
}

// Clear filter and close modal
const clearFilterAndClose = (filterId: FilterId | undefined): void => {
  clearFilter(filterId)
  closeFilterModal()
}

// Apply filter and close modal
const applyFilterAndClose = (filterId: FilterId | undefined): void => {
  applyFilter(filterId)
  closeFilterModal()
}

// Track which filters have been applied
const appliedFilters = ref<Set<FilterId>>(new Set())

// Filter options data
const filterOptions = ref<FilterOptions>({
  dietary: [],
  ratings: [
    { id: '5', label: '5 stars', value: null },
    { id: '4', label: '4 stars or more', value: null },
    { id: '3', label: '3 stars or more', value: null },
    { id: '2', label: '2 stars or more', value: null }
  ]
})

// Selected filter options (local state for filters with apply buttons)
const selectedOptions = ref<SelectedFilterOptions>({
  dietary: [],
  ratings: []
})

// Desktop filters
const filters = ref<FilterDefinition[]>([
  {
    hasDropdown: false,
    icon: SparklesIcon,
    iconColor: 'text-warning-400 fill-icon-current',
    id: 'reliability',
    isActive: false,
    label: 'Reliability Superstar'
  },
  {
    hasDropdown: true,
    icon: StarIcon,
    iconColor: 'text-warning-400 fill-icon-current',
    id: 'ratings',
    isActive: false,
    label: 'Ratings'
  },
  {
    hasDropdown: true,
    id: 'dietary',
    isActive: false,
    label: 'Dietary options'
  }
])

// Computed property to check if any modal filters are active
const isOtherFiltersActive = computed(() => {
  return filtersStore.selectedOtherFilters.length > 0 || filtersStore.selectedDistance !== null
})

// Handle filter application from modal
const handleApplyFilters = (filters: FilterModalPayload): void => {
  // Mark all filters as applied when modal applies them
  if (filters.dietary && filters.dietary.length > 0) {
    appliedFilters.value.add('dietary')
  }
  if (filters.otherFilters && filters.otherFilters.length > 0) {
    appliedFilters.value.add('other')
  }

  // Update filter active state
  updateActiveState()

  // Don't trigger search here - the search page watchers will handle it
  // when the filters store is updated by the modal

  // Close the modal
  showFilterModal.value = false
}

// Use the app data composable
const { dietaryOptions: appDietaryOptions, fetchAppData } = useAppData()

// Helper to map slug to icon name (same as store detail page)
const dietaryIconNames: Record<DietaryIconName, true> = {
  'dairy-free': true,
  'gluten-free': true,
  halal: true,
  keto: true,
  kosher: true,
  'low-carb': true,
  'nut-free': true,
  vegan: true,
  vegetarian: true
}

const isDietaryIconName = (value: string): value is DietaryIconName => {
  return Object.hasOwn(dietaryIconNames, value)
}

const getDietaryIconName = (slug: FilterValue): DietaryIconName | null => {
  const normalizedSlug = String(slug)
  return isDietaryIconName(normalizedSlug) ? normalizedSlug : null
}

// Load dietary options from app data
const loadDietaryOptions = (): void => {
  filterOptions.value.dietary = appDietaryOptions.value.map((option) => {
    const slug = option.slug != null && option.slug !== '' ? option.slug : option.id
    return {
      icon: getDietaryIconName(slug),
      id: option.id,
      label: option.label,
      value: null
    }
  })
}

const isFilterOptionGroupId = (filterId: FilterId | undefined): filterId is FilterOptionGroupId => {
  return filterId === 'ratings' || filterId === 'dietary'
}

const getFilterOptions = (filterId: FilterId | undefined): FilterOption[] => {
  if (!isFilterOptionGroupId(filterId)) return []
  return filterOptions.value[filterId]
}

const isOptionSelected = (filterId: FilterId | undefined, optionId: FilterValue): boolean => {
  const selectedOptionsByFilter: Partial<Record<FilterId, readonly FilterValue[]>> = {
    dietary: selectedOptions.value.dietary,
    other: filtersStore.selectedOtherFilters,
    ratings: filtersStore.selectedRatings
  }

  return filterId !== undefined && selectedOptionsByFilter[filterId]?.includes(optionId) === true
}

const toggleSelectedOption = (options: FilterValue[], optionId: FilterValue): void => {
  const index = options.indexOf(optionId)
  if (index > -1) {
    options.splice(index, 1)
    return
  }

  options.push(optionId)
}

const toggleFilterOption = (filterId: FilterId | undefined, optionId: FilterValue): void => {
  const optionToggles: Partial<Record<FilterId, (_option: FilterValue) => void>> = {
    dietary: (option) => {
      toggleSelectedOption(selectedOptions.value.dietary, option)
    },
    other: (option) => {
      filtersStore.toggleOtherFilter(String(option))
    },
    ratings: (option) => {
      filtersStore.setRating(option)
      updateActiveState()
    }
  }

  if (filterId !== undefined) optionToggles[filterId]?.(optionId)
}

const clearFilter = (filterId: FilterId | undefined): void => {
  const filterClearers: Partial<Record<FilterId, () => void>> = {
    dietary: () => {
      selectedOptions.value.dietary = []
      filtersStore.clearDietaryOptions()
    },
    other: () => {
      filtersStore.clearOtherFilters()
    },
    ratings: () => {
      filtersStore.clearRatings()
    }
  }

  if (filterId === undefined) return
  filterClearers[filterId]?.()
  appliedFilters.value.delete(filterId)
  updateActiveState()
}

const applyFilter = (filterId: FilterId | undefined): void => {
  const filterAppliers: Partial<Record<FilterId, () => void>> = {
    dietary: () => {
      filtersStore.selectedDietaryOptions = [...selectedOptions.value.dietary]
    }
  }

  if (filterId === undefined) return
  filterAppliers[filterId]?.()
  appliedFilters.value.add(filterId)
  updateActiveState()
}

// Use the filters store
const filtersStore = useFiltersStore()

// Update active state based on applied filters
const updateActiveState = (): void => {
  const activeFilterStates: Record<FilterId, boolean> = {
    dietary: filtersStore.selectedDietaryOptions.length > 0,
    other: filtersStore.selectedOtherFilters.length > 0,
    ratings: filtersStore.selectedRatings.length > 0,
    reliability: filtersStore.selectedReliability
  }

  filters.value.forEach((filter) => {
    if (filter.id === 'ratings') {
      filter.isActive = activeFilterStates.ratings
      return
    }

    filter.isActive = appliedFilters.value.has(filter.id) && activeFilterStates[filter.id]
  })
}

// Function to sync local state with store state
const syncLocalStateWithStore = (): void => {
  // Sync dietary options
  selectedOptions.value.dietary = [...filtersStore.selectedDietaryOptions]

  // Update applied filters based on store state
  if (filtersStore.selectedDietaryOptions.length > 0) {
    appliedFilters.value.add('dietary')
  }
  if (filtersStore.selectedOtherFilters.length > 0) {
    appliedFilters.value.add('other')
  }
  if (filtersStore.selectedReliability) {
    appliedFilters.value.add('reliability')
  }
}

// Watch for changes in the filters store to update button states
watch(
  () => [
    filtersStore.selectedRatings,
    filtersStore.selectedDietaryOptions,
    filtersStore.selectedOtherFilters,
    filtersStore.selectedReliability
  ],
  () => {
    updateActiveState()
    // Sync local state with store state
    syncLocalStateWithStore()
  },
  { deep: true, immediate: true }
)

// Initialize dietary options on mount
onMounted(async () => {
  // Fetch app data first, then load dietary options
  await fetchAppData()
  loadDietaryOptions()

  // Sync local state with store state
  syncLocalStateWithStore()

  // Update active state on mount to reflect current store state
  updateActiveState()
})

// Handle filter clicks for reliability
const handleFilterClick = (filter: FilterDefinition): void => {
  if (filter.id === 'reliability') {
    // For reliability filter, trigger search immediately
    filtersStore.toggleReliability()

    // Mark the filter as applied so it shows as active
    if (filtersStore.selectedReliability) {
      appliedFilters.value.add('reliability')
    } else {
      appliedFilters.value.delete('reliability')
    }
    updateActiveState()
  } else {
    // For other filters, emit toggle event
    emit('filterToggleRequested', filter)
  }
}
</script>

<style scoped>
/* Hide scrollbar for webkit browsers */
.scrollbar-hide {
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
}

.scrollbar-hide::-webkit-scrollbar {
  display: none; /* Chrome, Safari and Opera */
}

/* Smooth scrolling */
.overflow-x-auto {
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch; /* iOS smooth scrolling */
}
</style>
